<template>
  <filters-bar
    :available-filters="availableFilters"
    :filters="filters"
    :is-open="isOpen"
    :results="results"
    :header="t('iorder-filters-label')"
    :clear-btn="t('iorder-clear-button-label')"
    @on-close="closeFilterBar"
    @on-filter-enable="enableFilter"
    @on-filter-disable="disableFilter"
    @on-filter-clear="clearAllFilters"
  />
</template>

<script setup lang="ts">
import type { MenuFiltersProps } from './types';
import type { MenuProduct, MenuPortion } from '@/types/menu';
import type { MenuData, MenuFilter, MenuFilterValue } from '../types';
import { reactive, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import getFilters from './FiltersBase';
import FiltersBar from './../../2_elements/FiltersBar/FiltersBar.vue';

const { t } = useI18n();

const emit = defineEmits<{
  onFiltersChange: [filters: MenuFilter[]];
  onClose: [];
}>();

const FILTER_TYPE = { filters: 'filters', sort: 'sort', switch: 'switch' };
const DIET_OPTIONS = new Set(['vegetarian', 'vegan', 'gluten free']);
const FILTER_TYPES = ['Sort by', 'Diet', 'Calories', 'Others'];

const props = withDefaults(defineProps<MenuFiltersProps>(), {
  menuData: () => ({}) as MenuData,
});

const categoryValues = computed(() => {
  const min = props.caloriesMin ? Number.parseInt(props.caloriesMin) : 250;
  const max = props.caloriesMax ? Number.parseInt(props.caloriesMax) : 800;
  return min < max ? { min, max } : { min: max, max: min };
});

const menuItems = computed(
  () =>
    props.menuData?.activeMenuItem?.menuTree?.reduce((acc: MenuProduct[], item) => {
      if (item.children?.length) acc.push(...item.children);
      return acc;
    }, []) || []
);

const results = computed(() => menuItems.value?.length);

const availableFilters = computed(() => {
  const filters = menuItems.value.reduce(
    (acc: { calories: boolean; diet: string[] }, item: MenuProduct) => {
      item.keywords?.forEach((keyword: string) => {
        if (DIET_OPTIONS.has(keyword.toLowerCase()) && !acc.diet.includes(keyword))
          acc.diet.push(keyword);
      });

      item.portions?.forEach((portion: MenuPortion) => {
        if (portion.calories) acc.calories = true;
      });

      return acc;
    },
    { calories: false, diet: [] }
  );

  return {
    ...filters,
    shouldRenderFilter: (filter: MenuFilterValue, type?: string) => {
      if (type === 'sort by') return filter.name === 'Calories' ? filters.calories : true;
      if (type === 'calories') return filters.calories;
      if (type === 'others') return true;
      return !!filter.slug && filters.diet.includes(filter.slug);
    },
  };
});

const filters = reactive<MenuFilter[]>([]);

const enabledFilters = computed(() =>
  filters
    .map((set) => {
      const filters = set.values.filter((filter) => filter.enabled);
      return filters?.length ? { ...set, values: filters } : null;
    })
    .filter((set): set is MenuFilter => !!set)
);

const closeFilterBar = () => {
  emit('onClose');
};

const handleFilter = (item: MenuFilter, filter: string, value: boolean): MenuFilterValue[] => {
  if (item.type === FILTER_TYPE.sort)
    return item.values.map((val: MenuFilterValue) => ({ ...val, enabled: val.name === filter }));
  if (item.type === FILTER_TYPE.switch || item.type === FILTER_TYPE.filters)
    return item.values.map((val: MenuFilterValue) =>
      val.name === filter ? { ...val, enabled: value } : val
    );
  return item.values;
};

const enableFilter = ({ set, filter }: { set: string; filter: string }) => {
  filters.forEach((item) => {
    if (item.name === set) item.values = handleFilter(item, filter, true);
  });
};

const disableFilter = ({ set, filter }: { set: string; filter: string }) => {
  filters.forEach((item) => {
    if (item.name === set) item.values = handleFilter(item, filter, false);
  });
};

const clearAllFilters = () => {
  closeFilterBar();
  filters.forEach((item) => {
    if (item.type === FILTER_TYPE.filters)
      item.values = item.values.map((val: MenuFilterValue) => ({ ...val, enabled: false }));
  });
};

onMounted(() => {
  filters.push(...getFilters(FILTER_TYPES, categoryValues.value));
  emit('onFiltersChange', enabledFilters.value);
});

watch(filters, () => {
  emit('onFiltersChange', enabledFilters.value);
});
</script>

<style lang="scss">
@import './scss/MenuFilters.scss';
</style>
